import type { FC } from 'react';

import { Fragment } from 'react';
import styled from 'styled-components';

import { getAmplienceMeta, unmanagedComponents } from '../../../utilities/amplience';
import logger from '../../../utilities/logger';
import { Title } from '../title/Title';
import type { Moodbanner as MoodbannerType } from '~/amplienceTypes/schemas/exported/plp-schema';
import { media } from '~/components/shared/core/styles';
import { BaseStyled } from '~/components/unmanaged/styles/BaseStyled';
import { BaseStyledModifier, ColorScheme } from '~/components/unmanaged/styles/BaseStyledModifiers';
import { BaseStyledStyling } from '~/components/unmanaged/styles/BaseStyledStyling';
import { removeLegacyModifierClassNames } from '~/components/unmanaged/styles/utils';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  MoodBanner: styled.header`
    position: relative;
    overflow: hidden;

    ${BaseStyledStyling.BoxSizingBorder}

    ${BaseStyled.Banner}

    /* Inspirational (Default) */
    :where(.contentBlock) {
      --contentBlock-body-padding: 24px;
      --contentBlock-button-margin: var(--ds-spacing-sm, 16px);
      --contentBlock-text-align: left;
      --contentBlock-justify-content: flex-start;
      --contentBlock-align-items: flex-end;

      :where(.contentBlock-body-title),
      :where(.contentBlock-body-subTitle) {
        ${BaseStyledStyling.HeadingStyling}
        font-size: var(--banner-title-font-size, 24px);
      }

      :where(.contentBlock-body-subTitle) {
        font-size: var(--banner-subTitle-font-size, 24px);
      }

      :where(.contentBlock-body-text) {
        margin-block-start: var(--ds-spacing-sm, 16px);

        a {
          font-weight: 600;
          text-decoration: underline;
        }
      }

      @media ${({ theme }) => media(theme).greaterThan('sm')} {
        --contentBlock-aspect-ratio: 32/9;
        --contentBlock-body-padding: 40px;
        --contentBlock-button-margin: var(--ds-spacing-md, 24px);
      }

      @media ${({ theme }) => media(theme).greaterThan('tablet')} {
        --banner-title-font-size: var(--ds-heading-md, 32px);
        --banner-subTitle-font-size: var(--ds-heading-md, 32px);
      }

      @media ${({ theme }) => media(theme).greaterThan('xxl')} {
        --banner-title-font-size: clamp(32px, calc(32 * (100 / 1680) * 1vw), 64px);
      }
    }

    /* Inspirational Large */
    :where(.row--height400) {
      .contentBlock {
        --contentBlock-aspect-ratio: 1/1;
        --banner-title-font-size: var(--ds-heading-lg, 32px);
        --banner-subTitle-font-size: var(--ds-heading-lg, 32px);

        @media ${({ theme }) => media(theme).greaterThan('sm')} {
          --contentBlock-aspect-ratio: 4/2;
        }

        @media ${({ theme }) => media(theme).greaterThan('tablet')} {
          --banner-title-font-size: var(--ds-heading-lg, 48px);
          --banner-subTitle-font-size: var(--ds-heading-lg, 48px);
        }

        @media ${({ theme }) => media(theme).greaterThan('xxl')} {
          --banner-title-font-size: clamp(48px, calc(48 * (100 / 1680) * 1vw), 72px);
        }
      }
    }

    /* Editorial / Commerce */
    :where(.row:not(:has(.contentBlock-picture))),
    :where(.row[data-type='editorial']),
    :where(.row[data-type='commerce']) {
      :where(.contentBlock) {
        --contentBlock-body-padding: var(--ds-spacing-md, 24px) var(--ds-spacing-2xs, 8px) 0;
      }

      :where(.contentBlock-body-links) {
        margin-block-start: var(--ds-spacing-sm, 16px);
      }

      ${BaseStyledModifier.CategoryTags}

      .contentBlock {
        background-color: transparent;
      }

      .lockup-container {
        font-size: 200%;

        @media ${({ theme }) => media(theme).greaterThan('sm')} {
          font-size: 150%;
        }

        small {
          font-size: clamp(11px, 20%, 13px);
        }
      }

      ${({ theme }) =>
        getSiteStyles(theme.siteType, {
          [SiteType.Outlet]: ColorScheme.light,
        })}
    }

    :where(.row:not(:has(.contentBlock-picture))),
    :where(.row[data-type='commerce']) {
      :where(.contentBlock) {
        @media ${({ theme }) => media(theme).greaterThan('sm')} {
          --contentBlock-body-padding: var(--ds-spacing-md, 24px) var(--ds-spacing-sm, 16px) 0;
        }

        @media ${({ theme }) => media(theme).greaterThan('desktop-sm')} {
          --contentBlock-body-padding: var(--ds-spacing-lg, 32px) var(--ds-spacing-md, 24px) 0;
        }
      }
    }

    :where(.row[data-type='editorial']) {
      :where(.contentBlock) {
        --contentBlock-aspect-ratio: 4/3;
        --contentBlock-body-padding: 0 var(--ds-spacing-2xs, 8px);

        grid-template-rows: auto auto;
        gap: var(--ds-spacing-md, 24px);

        @media ${({ theme }) => media(theme).greaterThan('sm')} {
          --contentBlock-aspect-ratio: 2/1;
          --contentBlock-body-padding: 0 var(--ds-spacing-sm, 16px) 0 0;

          grid-template-rows: 1fr;
          grid-template-columns: 1fr 1fr;
          align-items: center;
        }
      }

      :where(.contentBlock-body) {
        grid-row-start: 2;

        @media ${({ theme }) => media(theme).greaterThan('sm')} {
          grid-area: 1 / 2;
        }
      }

      @media ${({ theme }) => media(theme).lessThan('sm')} {
        :where(.contentBlock:has(.lockup-container)) {
          --contentBlock-body-padding: 0 var(--ds-spacing-md, 24px);

          :where(.contentBlock-body) {
            --categoryTagScrolling-inline-padding: var(--ds-spacing-md, 24px);
          }
        }
      }
    }

    /* Promo/SALE Header */
    :where(.contentBlock:has(.lockup-container)) {
      ${BaseStyledModifier.Promo}
      ${BaseStyledModifier.CategoryTags}
    }

    /* Add Category Tag styling when banner not clickable and there are more then 3 buttons */
    :where(.row:not([data-type])) {
      /* prettier-ignore */
      :where(.contentBlock:not(.contentBlock--clickable):has(.contentBlock-picture):has(.contentBlock-body-link:nth-child(n + 3))){
        --categoryTag-grow-before: 0;
        --categoryTag-grow-after: 1;
        ${BaseStyledModifier.CategoryTags}

        :where(.contentBlock-body) {
          --categoryTagScrolling-inline-padding: var(--ds-spacing-md, 24px);
        }
      }
    }

    /* TODO: FS-3437 - PLP Moodbanners remove code for suppressing inline styling */
    .contentBlock-body-links {
      @media ${({ theme }) => media(theme).lessThan('sm')} {
        --bodyLinksMarginInline: calc(var(--categoryTagScrolling-inline-padding, 0) * -1) 0;
      }
    }

    .contentBlock-body-link {
      margin: 0 !important;
      line-height: 1.1 !important;
    }
  `,
};

type MoodbannerProps = {
  moodbanner?: MoodbannerType;
};

export const MoodBanner: FC<MoodbannerProps> = ({ moodbanner }) => {
  const bannerContent = moodbanner;

  return (
    <>
      {bannerContent?.length ? (
        <S.MoodBanner data-testid="moodbanner">
          {bannerContent
            /**
             * sorting is required because when navigating between lister pages with visually the same moodbanner
             * amplience content and inline js/css code
             * the list of amplience components might be in different order, and this will trigger flickering because
             * react will have to remount the components because even if they are the same, the order is different
             * this will trigger repaint
             * so this sorting guarantees the same order that is tracked by keys as `deliveryId`
             */
            .toSorted((a, b) =>
              (a._meta.deliveryId as string).localeCompare(b._meta.deliveryId as string)
            )
            .map((banner, i) => {
              const { metaAttributes, contentId, schema } = getAmplienceMeta(banner._meta);
              const key = `${contentId as string}-${i}`;
              const Component = schema ? unmanagedComponents[schema] : null;

              if (!Component) {
                logger.warn(`Component related to ${schema} does not exist.`);

                return <Fragment key={key} />;
              }

              /* TODO: FS-3437 - PLP Moodbanners remove code for suppressing inline styling */
              return (
                <Component
                  key={key}
                  {...metaAttributes}
                  {...removeLegacyModifierClassNames(banner)}
                />
              );
            })}
        </S.MoodBanner>
      ) : (
        <Title />
      )}
    </>
  );
};
